import { muiDataGridStarted } from "../apiutils";
import { apiSlice } from "../createApiSlice";

const WEBSHOP_BASE = "/webshop";
const buildUrl = (endpoint) => WEBSHOP_BASE + endpoint;
const ApiName = "WebshopCredentials";

const webshopSlice =  apiSlice.injectEndpoints({
    endpoints: builder => ({
        getWebshopCredentials: builder.query({
            query: ({ args }) => ({
                url: buildUrl(`/credentials?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: (result, error, arg) => {
                const allCredentials = arg.args?.body?.all_credentials ?? "no";
                const integrator = arg.args?.body?.integrator ?? "no";
                const relationId = arg.args?.body?.relationId ?? "no";
                
                return [
                    // General tag for all webshop credentials
                    { type: ApiName },
                    // Tag for specific all_credentials filter
                    { type: `${ApiName}-AllCredentials`, id: allCredentials },
                    // Tag for specific integrator filter
                    { type: `${ApiName}-Integrator`, id: integrator },
                    // Tag for specific relation
                    { type: `${ApiName}-Relation`, id: relationId },
                    // Tag for specific combination (for exact cache matching)
                    { type: `${ApiName}-Query`, id: `all-${allCredentials}-int-${integrator}-rel-${relationId}` }
                ];
            },
            onQueryStarted: muiDataGridStarted.bind(null, 'getWebshopCredentials', ApiName),
        }),
        getWebshopCredentialsSelect: builder.query({
            query: (arg) => {
                // Accept either a plain relationId or an object with additional options,
                // so existing callers passing just the relationId keep working.
                const { relationId, includeIntegrator = false } =
                    (arg && typeof arg === 'object') ? arg : { relationId: arg };
                return {
                    url: buildUrl(`/credentials/select`),
                    method: 'POST',
                    body: {
                        relationId,
                        includeIntegrator
                    },
                };
            },
            providesTags: () => [{ type: ApiName }],
        }),
        getWebshopCount: builder.query({
            query: (arg) => {
                // Callers that want the platform wide counts keep calling this without arguments.
                const relationId = (arg && typeof arg === 'object') ? arg.relationId : arg;
                return {
                    url: buildUrl(`/credentials/count${relationId ? `?relationId=${relationId}` : ``}`),
                    method: 'GET',
                };
            },
            providesTags: (result, error, arg) => {
                const relationId = ((arg && typeof arg === 'object') ? arg.relationId : arg) ?? "no";
                return [
                    { type: `${ApiName}Count` },
                    { type: `${ApiName}Count-Relation`, id: relationId }
                ];
            },
        }),
        getWebshopCredentialsSingle: builder.query({
            query: id => ({
                url: buildUrl(`/credentials/${id}`),
                method: 'GET',
                meta: { customContentType: true }, 
            }),
            providesTags: (result, error, id) => [{ type: 'WebshopCredentialsSingle', id: id }],
        }),
        editWebshopCredentials: builder.mutation({
            query: credentialsInfo => ({
                url: buildUrl(`/credentials/${credentialsInfo.id}`),
                method: 'PUT',
                body: credentialsInfo,
            }),
            invalidatesTags: (result, error, arg) => [
                { type: `WebshopCredentialsSingle`, id: arg.id },
                { type: ApiName },
                // Invalidate all query combinations since editing could affect any filter
                { type: `${ApiName}-AllCredentials` },
                { type: `${ApiName}-Integrator` },
                { type: `${ApiName}-Relation` },
                { type: `${ApiName}-Query` },
                { type: `${ApiName}Count` }
            ],
        }),
        bulkUpdateWebshopCredentials: builder.mutation({
            query: ({ ids, state }) => ({
                url: buildUrl(`/credentials/bulk-update`),
                method: 'PUT',
                body: { ids, state },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: ApiName },
                // Invalidate all query combinations since bulk updating could affect any filter
                { type: `${ApiName}-AllCredentials` },
                { type: `${ApiName}-Integrator` },
                { type: `${ApiName}-Relation` },
                { type: `${ApiName}-Query` },
                { type: `${ApiName}Count` },
                ...(arg.ids ?? []).map(id => ({ type: `WebshopCredentialsSingle`, id }))
            ],
        }),
        deleteWebshopCredentials: builder.mutation({
            query: id => ({
                url: buildUrl(`/credentials/${id}`),
                method: 'DELETE'
            }),
            invalidatesTags: (result, error, id) => [
                { type: `WebshopCredentialsSingle`, id: id },
                { type: ApiName },
                // Invalidate all query combinations since deleting affects all filters
                { type: `${ApiName}-AllCredentials` },
                { type: `${ApiName}-Integrator` },
                { type: `${ApiName}-Relation` },
                { type: `${ApiName}-Query` }
            ],
        }),
        createWebshopCredentials: builder.mutation({
            query: credentialsInfo => ({
                url: buildUrl(`/credentials/new`),
                method: 'POST',
                body: credentialsInfo,
            }),
            invalidatesTags: (result, error, arg) => [
                { type: ApiName },
                // Invalidate all query combinations since creating affects all filters
                { type: `${ApiName}-AllCredentials` },
                { type: `${ApiName}-Integrator` },
                { type: `${ApiName}-Relation` },
                { type: `${ApiName}-Query` }
            ],
        }),
        createWebshopCredentialsWithOAuth: builder.mutation({
            query: credentialsInfo => ({
                url: buildUrl(`/credentials/newauth`),
                method: 'POST',
                body: credentialsInfo,
            }),
            invalidatesTags: (result, error, arg) => [
                { type: ApiName },
                // Invalidate all query combinations since creating affects all filters
                { type: `${ApiName}-AllCredentials` },
                { type: `${ApiName}-Integrator` },
                { type: `${ApiName}-Relation` },
                { type: `${ApiName}-Query` }
            ],
        }),
        testWebshopConnection: builder.mutation({
            query: credentialsInfo => ({
                url: buildUrl(`/credentials/testconnection`),
                method: 'POST',
                body: credentialsInfo,
            }),
        }),
        syncWebshopProducts: builder.mutation({
            query: args => ({
                url: buildUrl(`/syncproducts/${args.id}`),
                method: 'POST',
                body: args,
            }),
            invalidatesTags: (result, error, arg) => [{type: `Products`}],
        }),
        getObjects: builder.query({
            query: fetchInfo => ({
                url: buildUrl(`/fetch/${fetchInfo.type}/${fetchInfo.id}`),
                method: 'GET'
            }),
        }),
        resolveWebshopCredentialsLogs: builder.mutation({
            query: ({ webshopCredentialsId, logIds }) => ({
                url: buildUrl(`/credentials/${webshopCredentialsId}/resolvelogs`),
                method: 'POST',
                body: { logIds },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: `LogsWebshopCredentials`, id: arg.webshopCredentialsId },
                { type: `WebshopCredentialsSingle`, id: arg.webshopCredentialsId }
            ],
        }),
        getWebshopProducts: builder.query({
            query: ({ args }) => ({
                url: buildUrl(`/credentials/${args.id}/products?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: (result, error, arg) => {
                const webshopCredentialsId = arg.args?.body?.webshopCredentialsId ?? "no";
                
                return [
                    // General tag for all webshop products
                    { type: 'WebshopProducts' },
                    // Tag for specific webshop credentials
                    { type: 'WebshopProducts-Credentials', id: webshopCredentialsId },
                ];
            },
            onQueryStarted: muiDataGridStarted.bind(null, 'getWebshopProducts', 'WebshopProducts'),
        }),
        createWebshopFromInstallation: builder.mutation({
            query: installationInfo => ({
                url: buildUrl(`/credentials/installation`),
                method: 'POST',
                body: installationInfo,
            }),
            invalidatesTags: (result, error, arg) => [
                { type: ApiName },
                // Invalidate all query combinations since creating affects all filters
                { type: `${ApiName}-AllCredentials` },
                { type: `${ApiName}-Integrator` },
                { type: `${ApiName}-Relation` },
                { type: `${ApiName}-Query` }
            ],
        }),
        syncWebshopChannel: builder.mutation({
            query:(data) => ({
                url: buildUrl(`/sync/${data.id}`),
                method:'POST',
                body:{type:data.type},
            })
        }),
        uploadWebshopLogo: builder.mutation({
            query: ({ id, file }) => {
                const formData = new FormData();
                formData.append('file', file);
                return {
                    url: buildUrl(`/${id}/uploadlogo`),
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: (result, error, arg) => [
                { type: `WebshopCredentialsSingle`, id: arg.id },
                { type: ApiName }
            ],
        }),
        syncImportProduct: builder.mutation({
            query: syncBody => ({
                url: buildUrl(`/${syncBody.id}/syncproduct`),
                method: 'PUT',
                body: syncBody,
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'WebshopProducts' },{ type: 'WebshopProducts-Credentials'}]
        }),
        ignoreProduct: builder.mutation({
            query: syncBody => ({
                url: buildUrl(`/${syncBody.id}/ignoreproduct`),
                method: 'PUT',
                body: syncBody,
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'WebshopProducts' },{ type: 'WebshopProducts-Credentials'}]
        }),
    })
});

export const {
    useGetWebshopCredentialsQuery,
    useGetWebshopCredentialsSelectQuery,
    useGetWebshopCredentialsSingleQuery,
    useEditWebshopCredentialsMutation,
    useBulkUpdateWebshopCredentialsMutation,
    useCreateWebshopCredentialsMutation,
    useSyncWebshopProductsMutation,
    useDeleteWebshopCredentialsMutation,
    useTestWebshopConnectionMutation,
    useResolveWebshopCredentialsLogsMutation,
    useCreateWebshopCredentialsWithOAuthMutation,
    useGetWebshopProductsQuery,
    useSyncImportProductMutation,
    useIgnoreProductMutation,
    useCreateWebshopFromInstallationMutation,
    useSyncWebshopChannelMutation,
    useUploadWebshopLogoMutation,
    useGetObjectsQuery,
    useGetWebshopCountQuery
} = webshopSlice;
